import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { usePageCms } from "@/lib/usePageCms";
import { PageHero, SectionHeading } from "@/components/site/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Loader2, ShieldCheck, Users, Lock, LifeBuoy, Handshake, Building2 } from "lucide-react";

const BUSINESS_TYPES = ["Travel Agency", "Tour Operator", "Independent Consultant", "Corporate Travel Planner", "MICE / Event Company", "Educational Institution", "Other"];
const VOLUMES = ["1–5 enquiries", "6–15 enquiries", "16–30 enquiries", "30+ enquiries"];

const WHO = ["Travel agencies", "Independent travel consultants", "Tour operators", "Corporate travel planners", "Group-tour organizers", "Educational institutions", "Event & MICE companies"];
const BENEFITS = [
  { icon: Handshake, title: "Customized Itineraries", text: "Programmes built around your client profiles, budget and duration." },
  { icon: Lock, title: "Confidential B2B Rates", text: "Competitive pricing prepared exclusively for travel partners." },
  { icon: LifeBuoy, title: "Ground-Operation Support", text: "Hotels, transport, guides and experiences handled on the ground." },
  { icon: Users, title: "Destination Updates", text: "Regular insights on seasons, new experiences and operational tips." },
];
const STEPS = ["Register as a partner", "Share your client requirement", "Receive a customized proposal", "Confirm and we manage the ground"];
const FAQS = [
  ["Do you work directly with my clients?", "No. We operate strictly B2B and behind the scenes. Your clients remain your clients at all times."],
  ["Do you publish prices?", "No. Confidential B2B rates are shared privately with registered partners on a per-enquiry basis."],
  ["What destinations do you cover?", "We specialise exclusively in Maharashtra — coast, forts, wildlife, hill stations and spiritual circuits."],
  ["How quickly will I receive a proposal?", "Turnaround depends on the complexity of the requirement; our team responds as promptly as possible."],
  ["Is there a cost to register as a partner?", "Registration is free. It simply adds you to our travel-partner network for proposals and updates."],
];

function Field({ label, required, children, error, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-accent text-xs font-semibold text-charcoal">{label}{required && <span className="text-saffron"> *</span>}</Label>
      {children}
      {error && <p data-testid={testid ? `${testid}-error` : undefined} className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default function PartnerWithUs() {
  const navigate = useNavigate();
  const PFB = { overline: "Travel Trade Partnership", title: "Grow Your Maharashtra Business with a Trusted Local Partner", subtitle: "Customized itineraries, confidential B2B rates and dependable ground operations — built for travel professionals.", image: "https://images.unsplash.com/photo-1663089554801-4562b8ea5388?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", seo_title: "Partner With Us | Maharashtra Ground Handler for Travel Agents | Maha Sampada", seo_description: "Join the Maha Sampada travel-partner network. Customized Maharashtra itineraries, confidential B2B rates and dependable ground operations — your clients stay yours." };
  const { hero, sections } = usePageCms("partner", PFB);
  useSeo({ title: hero.seo_title || PFB.seo_title, description: hero.seo_description || PFB.seo_description });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    agency_name: "", contact_person: "", designation: "", mobile: "", whatsapp: "", email: "",
    city: "", state: "", business_type: "", website: "", gst: "", destinations_interest: "",
    monthly_volume: "", message: "", consent: false,
  });
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.agency_name.trim()) e.agency_name = "Agency name is required";
    if (!form.contact_person.trim()) e.contact_person = "Contact person is required";
    if (!/^[0-9+\-\s]{7,15}$/.test(form.mobile.trim())) e.mobile = "Enter a valid mobile number";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = "Enter a valid email address";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.business_type) e.business_type = "Select a business type";
    if (!form.consent) e.consent = "Please provide consent to register";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      setTimeout(() => document.querySelector('[data-testid$="-error"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return;
    }
    setLoading(true);
    try {
      await api.post("/partners", form);
      toast.success("Registration submitted");
      navigate("/thank-you?type=partner");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally { setLoading(false); }
  };

  const PBEN = { handshake: Handshake, lock: Lock, "life-buoy": LifeBuoy, users: Users, "shield-check": ShieldCheck, "building-2": Building2 };
  const FormBlock = (introSection) => (
    <section key="partner-form" className="bg-white" data-testid="partner-registration-section">
      <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6">
        {introSection && <SectionHeading overline={introSection.overline} title={introSection.heading} center description={introSection.body} />}
        <form onSubmit={submit} data-testid="partner-form" className="mt-10 rounded-3xl border border-gold/25 bg-cream p-6 sm:p-10">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Agency Name" required error={errors.agency_name} testid="agency_name"><Input data-testid="input-agency-name" value={form.agency_name} onChange={(e) => set("agency_name", e.target.value)} /></Field>
            <Field label="Contact Person" required error={errors.contact_person} testid="contact_person"><Input data-testid="input-contact-person" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></Field>
            <Field label="Designation"><Input data-testid="input-designation" value={form.designation} onChange={(e) => set("designation", e.target.value)} /></Field>
            <Field label="Mobile Number" required error={errors.mobile} testid="mobile"><Input data-testid="input-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91 …" /></Field>
            <Field label="WhatsApp Number"><Input data-testid="input-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
            <Field label="Email Address" required error={errors.email} testid="email"><Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="City" required error={errors.city} testid="city"><Input data-testid="input-city" value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="State"><Input data-testid="input-state" value={form.state} onChange={(e) => set("state", e.target.value)} /></Field>
            <Field label="Business Type" required error={errors.business_type} testid="business_type">
              <Select value={form.business_type} onValueChange={(v) => set("business_type", v)}>
                <SelectTrigger data-testid="select-business-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{BUSINESS_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Website / Social Profile"><Input data-testid="input-website" value={form.website} onChange={(e) => set("website", e.target.value)} /></Field>
            <Field label="GST Number (optional)"><Input data-testid="input-gst" value={form.gst} onChange={(e) => set("gst", e.target.value)} /></Field>
            <Field label="Approx. Monthly Enquiry Volume">
              <Select value={form.monthly_volume} onValueChange={(v) => set("monthly_volume", v)}>
                <SelectTrigger data-testid="select-volume"><SelectValue placeholder="Select volume" /></SelectTrigger>
                <SelectContent>{VOLUMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="mt-5 grid gap-5">
            <Field label="Destinations of Interest"><Input data-testid="input-destinations-interest" value={form.destinations_interest} onChange={(e) => set("destinations_interest", e.target.value)} placeholder="e.g. Konkan, Tadoba, Ashtavinayak" /></Field>
            <Field label="Message"><Textarea data-testid="input-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} /></Field>
          </div>
          <label className="mt-6 flex items-start gap-3">
            <Checkbox data-testid="checkbox-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" />
            <span className="text-sm text-charcoal">I consent to Maha Sampada storing my details and contacting me regarding partnership.<span className="text-saffron"> *</span></span>
          </label>
          {errors.consent && <p data-testid="consent-error" className="text-xs text-destructive">{errors.consent}</p>}
          <Button type="submit" data-testid="submit-partner" disabled={loading} className="mt-8 w-full rounded-full bg-saffron font-accent font-semibold text-white hover:bg-saffron-dark sm:w-auto sm:px-10">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Registering…</> : "Register as a Travel Partner"}
          </Button>
        </form>
      </div>
    </section>
  );
  const R = {
    agent_protection: (s) => (
      <section key={s.section_id} data-testid="agent-protection-section" className="bg-forest">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-4 px-4 py-14 text-center sm:px-6">
          <ShieldCheck className="h-10 w-10 text-gold" />
          <div data-testid="agent-protection-statement" className="font-display text-2xl leading-relaxed text-cream sm:text-3xl" dangerouslySetInnerHTML={{ __html: s.intro_html || "" }} />
        </div>
      </section>
    ),
    chips: (s) => (
      <section key={s.section_id} data-testid="who-section" className="mx-auto max-w-7xl px-4 pt-20 sm:px-6">
        <SectionHeading overline={s.overline} title={s.heading} />
        <div className="mt-6 flex flex-wrap gap-2">{(s.items || []).map((w, i) => <span key={i} className="rounded-full border border-gold/30 bg-white px-4 py-2 font-accent text-xs text-charcoal">{w}</span>)}</div>
      </section>
    ),
    benefits: (s) => (
      <section key={s.section_id} data-testid="partner-benefits-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <SectionHeading overline={s.overline} title={s.heading} />
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {(s.benefits || []).filter((b) => b.visible !== false).map((b, i) => { const Icon = PBEN[b.icon] || Handshake; return (
            <div key={i} className="rounded-2xl border border-gold/25 bg-white p-5"><Icon className="h-6 w-6 text-saffron" /><h3 className="mt-3 font-display text-lg font-semibold text-charcoal">{b.title}</h3><p className="mt-1 text-sm text-muted-foreground">{b.text}</p></div>
          ); })}
        </div>
      </section>
    ),
    steps: (s) => (
      <section key={s.section_id} data-testid="partner-steps-section" className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
        <SectionHeading overline={s.overline} title={s.heading} center />
        <div className="mt-10 grid gap-6 md:grid-cols-4">
          {(s.steps || []).map((st, i) => (
            <div key={i} className="rounded-2xl border border-gold/25 bg-white p-6 text-center"><div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-maroon font-display text-xl font-bold text-cream">{st.n || i + 1}</div><p className="mt-4 text-sm text-charcoal">{st.title}</p></div>
          ))}
        </div>
      </section>
    ),
    form_intro: () => null,
    faqs: (s) => (
      <section key={s.section_id} data-testid="partner-faq-section" className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <SectionHeading overline={s.overline} title={s.heading} center />
        <Accordion type="single" collapsible className="mt-8" data-testid="partner-faq">
          {(s.items || []).filter((f) => f.active !== false).map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-gold/25">
              <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-display text-lg text-charcoal">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    ),
    cta: (s) => (
      <section key={s.section_id} data-testid="partner-final-cta-section" className="bg-forest">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6">
          <h2 className="font-display text-3xl font-bold text-cream sm:text-4xl">{s.heading}</h2>
          {s.body && <p className="mx-auto mt-4 max-w-xl text-sm text-cream/85">{s.body}</p>}
          {s.cta && s.cta.href && <Link to={s.cta.href} className="mt-8 inline-block"><Button size="lg" className="rounded-full bg-saffron px-8 font-accent font-semibold text-white hover:bg-saffron-dark">{s.cta.label}</Button></Link>}
        </div>
      </section>
    ),
  };

  const introSection = (sections || []).find((s) => s.kind === "form_intro");
  const anchorId = introSection ? introSection.section_id : ((sections || []).filter((s) => s.kind === "steps").slice(-1)[0]?.section_id);
  const out = [];
  let formPlaced = false;
  (sections || []).forEach((s) => {
    if (R[s.kind]) { const el = R[s.kind](s); if (el) out.push(el); }
    if (!formPlaced && s.section_id === anchorId) { out.push(FormBlock(introSection)); formPlaced = true; }
  });
  if (!formPlaced) out.push(FormBlock(introSection)); // protected form is always rendered
  out.push(
    <section key="sp-cta" className="bg-cream" data-testid="partner-service-provider-cta">
      <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 px-4 py-14 text-center sm:px-6">
        <Building2 className="h-9 w-9 text-saffron" />
        <h2 className="font-display text-2xl font-bold text-charcoal sm:text-3xl">Are you a Maharashtra service provider?</h2>
        <p className="max-w-2xl text-sm text-muted-foreground">Hotels, resorts, homestays, villas, activity and transport providers and tour guides can express interest in working with us. This is an expression of interest only — not an approval or contract.</p>
        <Link to="/service-provider-registration" data-testid="partner-to-service-provider">
          <Button size="lg" className="rounded-full bg-forest px-8 font-accent font-semibold text-white hover:bg-forest/90">Register as a Service Provider</Button>
        </Link>
      </div>
    </section>
  );

  return (
    <div>
      <PageHero overline={hero.overline} title={hero.title} subtitle={hero.subtitle} image={hero.image} crumbs={[{ label: "Partner With Us" }]} />
      {out}
    </div>
  );
}
